import axios, { type AxiosInstance } from 'axios';

import { toApiException } from '@/common/error.ts';

/**
 * CSRF for the Go services (shared `CSRFMiddleware`). Each service checks POST, PUT, PATCH and
 * DELETE in its signed-in routes: the `X-XSRF-TOKEN` header has to match the HttpOnly `_csrf`
 * cookie that `GET /api/v1/<service>/csrf` sets and returns as text. The token belongs to the
 * cookie, not to the user or the access token, so a token refresh keeps it.
 *
 * In a browser the web app shares the API's origin and the backend lets `Sec-Fetch-Site:
 * same-origin` writes through, so the header only matters for the native apps, whose requests go
 * through CapacitorHttp without that header. Sent anyway, it is ignored.
 */

const HEADER = 'X-XSRF-TOKEN';
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const SERVICE_PATH = '/api/v1/';
const FETCH_TIMEOUT = 15000;

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Set on the one replay after a refused token, so a second refusal is final. */
    csrfRetried?: boolean;
  }
}

// Per service root: the token, or null for a service that runs without CSRF.
const tokens = new Map<string, Promise<null | string>>();
// Services on one host share the `_csrf` cookie and a fetch without it mints a new one, so the
// fetches go one at a time: the first sets the cookie and the rest return the same token.
let queue: Promise<unknown> = Promise.resolve();

export const clearCsrfTokens = (): void => {
  tokens.clear();
};

/** `https://host/api/v1/<service>` for a request URL, or null for anything else. */
export const csrfServiceRoot = (uri: string): null | string => {
  let url: URL;
  try {
    url = new URL(uri, typeof window === 'undefined' ? undefined : window.location.href);
  } catch {
    return null;
  }
  const at = url.pathname.indexOf(SERVICE_PATH);
  if (at < 0) return null;
  const [service] = url.pathname.slice(at + SERVICE_PATH.length).split('/');
  return service ? `${url.origin}${url.pathname.slice(0, at)}${SERVICE_PATH}${service}` : null;
};

const fetchToken = async (root: string, authorization: string): Promise<null | string> => {
  try {
    const response = await axios.get<string>(`${root}/csrf`, {
      headers: { Authorization: authorization },
      responseType: 'text',
      transformResponse: (data: unknown) => data,
      timeout: FETCH_TIMEOUT,
    });
    // Without CSRF the path can land on another route (`GET /:id`), which answers in JSON.
    const type = String(response.headers['content-type'] ?? '');
    return type.startsWith('text/plain') && typeof response.data === 'string' && response.data
      ? response.data
      : null;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
};

/** The cached token, fetched on first use. A failed fetch is not cached: null for this request. */
const tokenFor = (root: string, authorization: string): Promise<null | string> => {
  let token = tokens.get(root);
  if (!token) {
    const fetched = queue.then(() => fetchToken(root, authorization));
    queue = fetched.catch(() => undefined);
    fetched.catch(() => {
      if (tokens.get(root) === fetched) tokens.delete(root);
    });
    tokens.set(root, fetched);
    token = fetched;
  }
  return token.catch(() => null);
};

const isCsrfRefusal = async (error: unknown): Promise<boolean> => {
  if (!axios.isAxiosError(error) || error.response?.status !== 401) return false;
  const exception = await toApiException(error.response);
  return exception?.domain === 'MIDDLEWARES' && exception.type === 'CSRF';
};

/**
 * Sends the service's token with every write and, when the service refuses it (an expired or
 * replaced cookie), fetches a new one and replays the request once.
 */
export const attachCsrf = (api: AxiosInstance): void => {
  api.interceptors.request.use(async (config) => {
    const authorization = config.headers.Authorization;
    // Signed out, only public routes answer, and those take no token.
    if (!UNSAFE_METHODS.has((config.method ?? '').toUpperCase()) || !authorization) return config;
    const root = csrfServiceRoot(api.getUri(config));
    if (!root) return config;
    const token = await tokenFor(root, String(authorization));
    if (token) config.headers.set(HEADER, token);
    return config;
  });

  api.interceptors.response.use(undefined, async (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.config || error.config.csrfRetried) throw error;
    const { config } = error;
    const authorization = config.headers.Authorization;
    const root = csrfServiceRoot(api.getUri(config));
    if (!root || !authorization || !(await isCsrfRefusal(error))) throw error;

    // Drop the cached token only when it is the refused one: a parallel refusal may have
    // already replaced it.
    const sent = config.headers.get(HEADER) ?? null;
    const cached = tokens.get(root);
    if (cached && (await cached.catch(() => undefined)) === sent && tokens.get(root) === cached) {
      tokens.delete(root);
    }
    const fresh = await tokenFor(root, String(authorization));
    if (!fresh || fresh === sent) throw error;
    return api.request({ ...config, csrfRetried: true });
  });
};
